import {
  Modal,
  Button,
  TextInput,
  Select,
  Stack,
  PasswordInput,
  Checkbox,
  Loader,
  Text,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useEffect } from 'react';
import api from '../api/axiosConfig';

export default function AddUserModal({ opened, close, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [rolesData, setRolesData] = useState([]);
  const [employeesData, setEmployeesData] = useState([]);
  const [fetchingRoles, setFetchingRoles] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);

  const form = useForm({
    initialValues: {
      username: '',
      employee_id: '',
      password: '',
      role: '',
      send_email: true,
    },
    validate: {
      username: (value) =>
        value.trim().length < 3 ? 'Kullanıcı adı en az 3 karakter olmalı' : null,
      employee_id: (value) => (!value ? 'Lütfen bir personel seçin' : null),
      password: (value) =>
        value.trim().length < 4 ? 'Şifre en az 4 karakter olmalı' : null,
      role: (value) => (!value ? 'Lütfen bir yetki rolü seçin' : null),
    },
  });

  useEffect(() => {
    if (!opened) return;

    const fetchRoles = async () => {
      setFetchingRoles(true);
      try {
        const res = await api.get('/admin/roles');
        const formattedRoles = res.data.map((role) => ({
          value: role.name,
          label: role.description
            ? `${role.description} (${role.name})`
            : role.name,
        }));
        setRolesData(formattedRoles);
      } catch (error) {
        console.error('Roller çekilirken hata oluştu:', error);
        setRolesData([]);
      } finally {
        setFetchingRoles(false);
      }
    };

    const fetchEmployeesWithoutUser = async () => {
      setFetchingEmployees(true);
      try {
        // Backend tarafında bunu eklemeni öneririm:
        // GET /employees/without-user
        const res = await api.get('/admin/without-user');

        const formattedEmployees = res.data.map((employee) => ({
          value: String(employee.id),
          label: `${employee.first_name} ${employee.last_name} ${
            employee.employee_type ? `- ${employee.employee_type}` : ''
          }${employee.position ? ` / ${employee.position}` : ''}${
            employee.site_name ? ` / ${employee.site_name}` : ''
          }`,
        }));

        setEmployeesData(formattedEmployees);
      } catch (error) {
        console.error('Personeller çekilirken hata oluştu:', error);
        setEmployeesData([]);
      } finally {
        setFetchingEmployees(false);
      }
    };

    fetchRoles();
    fetchEmployeesWithoutUser();
  }, [opened]);

  const handleClose = () => {
    form.reset();
    close();
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    try {
      const payload = {
        username: values.username.trim(),
        password: values.password,
        employee_id: Number(values.employee_id),
        role: values.role,
        send_email: values.send_email,
      };

      await api.post('/admin/users/', payload);

      form.reset();
      onSuccess?.();
      close();
    } catch (error) {
      console.error('Kullanıcı ekleme hatası:', error);
      const errorMsg =
        error.response?.data?.detail || 'Kullanıcı oluşturulurken bir hata oluştu.';
      alert('Hata: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Yeni Kullanıcı Oluştur"
      centered
      radius="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <Select
            label="Personel"
            placeholder={
              fetchingEmployees ? 'Personeller yükleniyor...' : 'Bir personel seçin'
            }
            data={employeesData}
            searchable
            clearable
            required
            disabled={fetchingEmployees || loading}
            nothingFoundMessage="Uygun personel bulunamadı"
            rightSection={fetchingEmployees ? <Loader size="xs" /> : null}
            {...form.getInputProps('employee_id')}
          />

          <TextInput
            label="Kullanıcı Adı"
            placeholder="erenisler"
            required
            disabled={loading}
            {...form.getInputProps('username')}
          />

          <Select
            label="Yetki Rolü"
            placeholder={fetchingRoles ? 'Roller yükleniyor...' : 'Bir rol seçin'}
            data={rolesData}
            searchable
            clearable
            required
            disabled={fetchingRoles || loading}
            nothingFoundMessage="Rol bulunamadı"
            rightSection={fetchingRoles ? <Loader size="xs" /> : null}
            {...form.getInputProps('role')}
          />

          <PasswordInput
            label="Geçici Şifre"
            placeholder="Geçici şifre girin"
            description="Kullanıcı ilk girişten sonra şifresini değiştirebilir."
            required
            disabled={loading}
            {...form.getInputProps('password')}
          />

          <Checkbox
            mt="xs"
            label="Giriş bilgilerini personelin e-posta adresine gönder"
            disabled={loading}
            {...form.getInputProps('send_email', { type: 'checkbox' })}
          />

          {employeesData.length === 0 && !fetchingEmployees && (
            <Text c="dimmed" size="sm">
              Kullanıcı hesabı atanabilecek uygun personel bulunamadı.
            </Text>
          )}

          <Button
            type="submit"
            fullWidth
            mt="md"
            loading={loading}
            color="blue"
            disabled={fetchingEmployees || fetchingRoles}
          >
            Kullanıcıyı Oluştur
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}